/* eslint-disable sonarjs/cognitive-complexity */
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Action } from '@ngrx/store';
import {
  UserActionTypes,
  GetUserProfileSuccess,
  GetUserProfileError,
  GetLoginUserProfileSuccess,
  GetLoginUserProfileError,
  UpdateUserProfile,
  UpdateUserProfileError,
  GetSecurityQuestionsSuccess,
  GetSecurityQuestionsError,
  UpdateUserProfileSuccess,
  UpdateSkipCount,
  UpdateSkipCountSuccess,
  UpdateSkipCountError,
  UpdateUserProfileIndicator,
  UpdateWatchAuditSuccess,
  UpdateWatchAuditError,
  UpdateLoginAuditSuccess,
  UpdateLoginAuditError,
  GetAuthMethodsStatusSuccess,
  GetAuthMethodsStatusError,
  GetUserAlerts,
  GetUserAlertsSuccess,
  GetUserAlertsError,
  UpdateUserAlerts,
  UpdateUserAlertsSuccess,
  UpdateUserAlertsError,
} from './user.actions';

import { of, Observable } from 'rxjs';
import { map, switchMap, catchError, concatMap, tap } from 'rxjs/operators';

import {
  AuthMethod,
  LoginProfileResponseEks,
  UserProfile,
} from '../user-profile/user-profile.model';
import { UserProfileService } from '../user-profile/user-profile.service';
import { UserSecurityQuestionsService } from '../user-security-questions/security-questions.service';
import { ShellTrackHandlers } from '@cigna/chcp/shared/analytics-util';
import { AppContext } from '@cigna/chcp/shared/util';
import { APP_CONTEXT } from '@cigna/chcp/shared/environment-util';
import { UserAlertsService } from '../user-alerts/user-alerts.service';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private userProfileService: UserProfileService,
    private securityQuestionService: UserSecurityQuestionsService,
    private userAlertsService: UserAlertsService,
    private analytics: ShellTrackHandlers,
    private router: Router,
    @Inject(APP_CONTEXT) public appContext: AppContext,
  ) {}

  getAuthMethodsStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.GetAuthMethodsStatus),
      switchMap(() =>
        this.userProfileService.getAuthMethodsStatus().pipe(
          map((data: AuthMethod[]) => new GetAuthMethodsStatusSuccess(data)),
          catchError(() => of(new GetAuthMethodsStatusError())),
        ),
      ),
    ),
  );

  getUserProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.GetUserProfile),
      switchMap(() =>
        this.userProfileService.getUserProfile().pipe(
          map((data: any) => new GetUserProfileSuccess(data)),
          catchError((err) => of(new GetUserProfileError(err))),
        ),
      ),
    ),
  );

  getUserAlerts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.GetUserAlerts),
      map((action: GetUserAlerts) => action.payload),
      switchMap((status) =>
        this.userAlertsService.getUserAlerts(status).pipe(
          map((alerts) => new GetUserAlertsSuccess(alerts)),
          catchError((error) => of(new GetUserAlertsError(error))),
        ),
      ),
    ),
  );

  updateUserAlerts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.UpdateUserAlerts),
      map((action: UpdateUserAlerts) => action.payload),
      concatMap((updates) =>
        this.userAlertsService.updateUserAlerts(updates).pipe(
          map(() => new UpdateUserAlertsSuccess(updates)),
          catchError((error) => of(new UpdateUserAlertsError(error))),
        ),
      ),
    ),
  );

  getUserProfileServiceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActionTypes.GetUserProfileError),
        map((action: GetUserProfileError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/identity/silhouette/v3:GET',
            error,
            'Error loading user profile.',
          );
          this.router.navigateByUrl('/unavailable', {
            skipLocationChange: true,
          });
        }),
      ),
    { dispatch: false },
  );

  getLoginUserProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.GetLoginUserProfile),
      switchMap(() =>
        this.userProfileService.getLoginUserProfile().pipe(
          map(
            (data: LoginProfileResponseEks) =>
              new GetLoginUserProfileSuccess(data),
          ),
          catchError((err) => of(new GetLoginUserProfileError(err))),
        ),
      ),
    ),
  );

  getLoginUserProfileServiceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActionTypes.GetLoginUserProfileError),
        map((action: GetLoginUserProfileError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            `/identity/silhouette/v3:GET`,
            error,
            'Error loading login user profile.',
          );
        }),
      ),
    { dispatch: false },
  );

  updateUserProfile$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.UpdateUserProfile),
      map((action: UpdateUserProfile) => action.payload),
      concatMap((userData) =>
        this.userProfileService.updateUserProfile(userData).pipe(
          map((data: UserProfile) => new UpdateUserProfileSuccess(data)),
          catchError((err) => of(new UpdateUserProfileError(err))),
        ),
      ),
    ),
  );

  updateUserAgreementIndicator$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.UpdateUserProfileIndicator),
      map((action: UpdateUserProfileIndicator) => action.payload),
      concatMap((userData) =>
        this.userProfileService.updateUserProfileIndicators(userData).pipe(
          map(
            (data: Partial<UserProfile>) => new UpdateUserProfileSuccess(data),
          ),
          catchError((err) => of(new UpdateUserProfileError(err))),
        ),
      ),
    ),
  );

  updateUserProfileServiceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActionTypes.UpdateUserProfileError),
        map((action: UpdateUserProfileError) => action.payload),
        tap((error) => {
          this.analytics.trackServiceError(
            {},
            'chcp/preferences/v2/details:PUT',
            error,
            'Error updating user profile.',
          );
        }),
      ),
    { dispatch: false },
  );

  updateWatchAudit$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.UpdateWatchAudit),
      switchMap(() =>
        this.userProfileService.updateWatchAudit().pipe(
          map(() => new UpdateWatchAuditSuccess()),
          catchError((err) => of(new UpdateWatchAuditError(err))),
        ),
      ),
    ),
  );

  updateLoginAudit$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.UpdateLoginAudit),
      switchMap(() =>
        this.userProfileService.updateLoginAudit().pipe(
          map(() => new UpdateLoginAuditSuccess()),
          catchError((err) => of(new UpdateLoginAuditError(err))),
        ),
      ),
    ),
  );

  getSecurityQuestions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.GetSecurityQuestions),
      switchMap(() =>
        this.securityQuestionService.getSecurityQuestions().pipe(
          map((result) => new GetSecurityQuestionsSuccess(result)),
          catchError((err) => of(new GetSecurityQuestionsError(err))),
        ),
      ),
    ),
  );

  updateSkipCount$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.UpdateSkipCount),
      map((action: UpdateSkipCount) => action.payload),
      concatMap((userData) =>
        this.userProfileService
          .updateUserProfile(
            userData.type === 'popup'
              ? { contactCntr: userData.count }
              : { emailVerifySkipCount: userData.count },
          )
          .pipe(
            map((data: UserProfile) => new UpdateSkipCountSuccess(data)),
            catchError((_err) => of(new UpdateSkipCountError())),
          ),
      ),
    ),
  );
}
